/**
 * The status vocabulary of the claim details page, in one place.
 *
 * Three different lists on this screen (payments, documents, offers) each carry
 * a status pill, and each arrives from CRM with its own field and its own
 * encoding — `IsSuccess`, `FileUploaded`, `Offer_Status`. Previously every list
 * re-derived its own label and colour inline, which is how "Paid" ended up
 * green while "Completed" used the same green for a different meaning and the
 * Arabic build showed English words. Describing them here keeps one tone scale
 * across the page and gives every label both languages.
 */

// Tinted rather than solid-filled. Up to a dozen pills can be on screen at
// once; a column of saturated blocks reads as a page full of alarms. The inset
// ring keeps each one legible against the #F6F8F9 surface behind the cards.
export const STATUS_TONES = {
  success: "bg-[#E6F6EA] text-[#0F7A22] ring-1 ring-inset ring-[#B7E4C2]",
  pending: "bg-[#FFF3E3] text-[#8A5300] ring-1 ring-inset ring-[#F6D2A1]",
  danger: "bg-[#FDECEB] text-[#B3130F] ring-1 ring-inset ring-[#F6C3C1]",
  info: "bg-[#E4F5FD] text-[#00668F] ring-1 ring-inset ring-[#B3E2F6]",
  neutral: "bg-[#F0F3F5] text-[#5B6871] ring-1 ring-inset ring-[#DDE2E4]",
};

/**
 * CRM's `Claim.ClaimStatus` bucketed into the five stages the customer sees.
 *
 * This is the single source of truth for that bucketing. The same sixteen-case
 * mapping was written out longhand in two places — as chained switch statements
 * on the details page and again in the claims list card — which is how the two
 * screens ended up able to disagree about which stage a claim was in.
 *
 * Two behaviours from the original are preserved deliberately: "Rejected Claim"
 * sits on the Intimation stage rather than getting one of its own, and an
 * unrecognised status lands on Registration rather than on the first stage.
 *
 * The stage keys are also the CSS class names that colour the pill
 * (`.Intimation`, `.Settlement`, … in index.css), so they are English
 * identifiers and must not be translated — see `resolveClaimStage`.
 */
const CRM_STATUS_TO_STAGE = {
  "Claim Intimation": "Intimation",
  "Intimation Acknowledgement": "Intimation",
  "Incomplete Intimation": "Intimation",
  "Rejected Claim": "Intimation",
  "Claim Registration": "Registration",
  "Pending for Deductible": "Registration",
  "Survey Completion": "Survey",
  "Pending for Estimate": "Survey",
  "Pending for Depreciation": "Survey",
  "Under Procurement": "Settlement",
  "Under Repair": "Settlement",
  "Total Loss": "Settlement",
  "Cash Loss": "Settlement",
  "Pending For Loss Adjustor": "Settlement",
  "Vehicle Collection": "Settlement",
  "Close Claim": "Closed",
};

const STAGE_LABELS = {
  Intimation: { en: "Intimation", ar: "الإبلاغ" },
  Registration: { en: "Registration", ar: "التسجيل" },
  Survey: { en: "Survey", ar: "الاستبيان" },
  Settlement: { en: "Settlement", ar: "التسوية" },
  Closed: { en: "Closed", ar: "مغلق" },
};

const STAGE_TO_STEP_KEY = {
  Intimation: "STEP_NAME_INTIMATION",
  Registration: "STEP_NAME_REGISTRATION",
  Survey: "STEP_NAME_SURVEY",
  Settlement: "STEP_NAME_SETTLEMENT",
  Closed: "STEP_NAME_CLOSE",
};

const FALLBACK_STAGE = "Registration";

/**
 * The stage a claim is in, with the label to print and the class that colours
 * it — the same `.claim-item .{stage}` pair the claims list uses, so a claim is
 * the same colour on the card you tap and on the page it opens.
 *
 * `key` is deliberately the untranslated stage name. The list used to build its
 * class from the *translated* label, so in Arabic the class was "الإبلاغ",
 * matched no rule, and the pill rendered white-on-transparent — invisible.
 */
export const resolveClaimStage = (crmStatus) => {
  const key = CRM_STATUS_TO_STAGE[crmStatus];
  if (!key) {
    return {
      key: null,
      label_en: "Unknown status.",
      label_ar: "حالة غير معروفة",
    };
  }
  return { key, label_en: STAGE_LABELS[key].en, label_ar: STAGE_LABELS[key].ar };
};

export const resolveActiveStepKey = (crmStatus) =>
  STAGE_TO_STEP_KEY[CRM_STATUS_TO_STAGE[crmStatus] || FALLBACK_STAGE];

/**
 * The raw CRM status, rendered for a human. CRM only speaks English, so the
 * Arabic side is supplied here — without it the header pill was the one part of
 * an Arabic page still reading "Pending For Loss Adjustor".
 *
 * An unknown status falls back to showing CRM's own string: wrong language
 * beats an empty pill, and new statuses appear without a release.
 */
const CRM_STATUS_LABELS = {
  "Claim Intimation": { ar: "إخطار بالمطالبة", tone: "info" },
  "Intimation Acknowledgement": { ar: "تأكيد الإخطار", tone: "info" },
  "Incomplete Intimation": { ar: "إخطار غير مكتمل", tone: "pending" },
  "Rejected Claim": { ar: "مطالبة مرفوضة", tone: "danger" },
  "Claim Registration": { ar: "تسجيل المطالبة", tone: "info" },
  "Pending for Deductible": { ar: "بانتظار دفع التحمل", tone: "pending" },
  "Survey Completion": { ar: "اكتمال المعاينة", tone: "info" },
  "Pending for Estimate": { ar: "بانتظار التقدير", tone: "pending" },
  "Pending for Depreciation": { ar: "بانتظار الاستهلاك", tone: "pending" },
  "Under Procurement": { ar: "قيد توفير القطع", tone: "info" },
  "Under Repair": { ar: "قيد الإصلاح", tone: "info" },
  "Total Loss": { ar: "خسارة كلية", tone: "info" },
  "Cash Loss": { ar: "تسوية نقدية", tone: "info" },
  "Pending For Loss Adjustor": { ar: "بانتظار مقدّر الخسائر", tone: "pending" },
  "Vehicle Collection": { ar: "استلام المركبة", tone: "info" },
  "Close Claim": { ar: "مطالبة مغلقة", tone: "success" },
};

export const describeClaimStatus = (crmStatus) => {
  if (!crmStatus) return null;
  const known = CRM_STATUS_LABELS[crmStatus];
  return {
    label_en: crmStatus,
    label_ar: known?.ar || crmStatus,
    tone: known?.tone || "neutral",
  };
};

/**
 * Per-list status descriptors.
 *
 * Each returns the pill, plus `actionable` — whether the row does anything when
 * tapped. The API expresses "not done yet" as the string "False" on three
 * differently-named fields, and note that payments pill off `IsSuccess` while
 * their tappability comes from `IsPaid`; those are genuinely two different
 * things (a submitted-but-unconfirmed payment is neither paid nor successful)
 * and the original behaviour is kept.
 */
export const describePayment = (payment) => {
  const paid = payment?.IsSuccess !== "False";
  return {
    tone: paid ? "success" : "pending",
    label_en: paid ? "Paid" : "Pending payment",
    label_ar: paid ? "مدفوع" : "بانتظار الدفع",
    actionable: payment?.IsPaid === "False",
  };
};

export const describeDocument = (doc) => {
  const uploaded = doc?.FileUploaded === "True";
  return {
    tone: uploaded ? "success" : "pending",
    label_en: uploaded ? "Completed" : "Pending upload",
    label_ar: uploaded ? "مكتمل" : "بانتظار الرفع",
    actionable: doc?.FileUploaded === "False",
  };
};

const OFFER_STATES = {
  1: { tone: "success", en: "Approved", ar: "تمت الموافقة" },
  2: { tone: "danger", en: "Rejected", ar: "مرفوض" },
  3: { tone: "info", en: "Requested", ar: "تم الطلب" },
};

export const describeOffer = (offer) => {
  const state = OFFER_STATES[offer?.Offer_Status];
  return {
    tone: state?.tone || "pending",
    label_en: state?.en || "Pending",
    label_ar: state?.ar || "قيد الانتظار",
    // Only an untouched offer ("0") can be answered. Compared as a string
    // because CRM has sent this field both ways; the original code used `==`
    // throughout, and a strict check here would silently make every pending
    // offer unanswerable the day it arrives as a number.
    actionable: String(offer?.Offer_Status) === "0",
  };
};

/**
 * CRM sends the claim-history SMS as one string holding the English and Arabic
 * copies back to back, with nothing but the script change to separate them.
 *
 * The original split it inline with a regex whose result was destructured
 * without a null check, so a message CRM had not filled in took the page down
 * with it. Same split, guarded, and returning a shape the caller can hand to
 * `translate()` — the customer reads one language, not both stacked.
 */
export const splitBilingualSms = (content) => {
  const raw = typeof content === "string" ? content.trim() : "";
  if (!raw) return { en: "", ar: "" };

  const firstArabic = raw.search(/[؀-ۿ]/);
  if (firstArabic === -1) return { en: raw, ar: "" };
  if (firstArabic === 0) return { en: "", ar: raw };

  return {
    en: raw.slice(0, firstArabic).trim(),
    ar: raw.slice(firstArabic).trim(),
  };
};

/**
 * CRM timestamps come through with seconds on them — "23/07/2024 09:15:22" —
 * and are rendered as-is, because the format is a display string from CRM
 * rather than anything this app parses.
 *
 * Seconds are noise on a claim: nothing here happens twice in the same minute,
 * and the extra ":22" only makes the line harder to scan. This strips them
 * without needing to know the date format, by rewriting the time portion alone
 * — a value carrying no time is returned untouched, and so is one this does not
 * recognise, so an unexpected format degrades to today's behaviour rather than
 * to an empty cell.
 */
export const formatCrmTimestamp = (value) => {
  if (!value) return "";
  return String(value)
    .replace(/(\d{1,2}:\d{2}):\d{2}(?:\.\d+)?/, "$1")
    .trim();
};

export const PAYMENT_TYPE_LABELS = {
  excess: { en: "Collection of Deductible", ar: "تحصيل مبلغ التحمل" },
  depreciation: { en: "Collection of Depreciation", ar: "تحصيل الاستهلاك" },
};

export const describePaymentType = (type) =>
  PAYMENT_TYPE_LABELS[type] || { en: "General Payment", ar: "دفعة عامة" };
